import React, { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, ArrowRight, CheckCircle, ClipboardList, Loader2, Search, UserRound } from 'lucide-react'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'

export type DynamicFormValue = string | number | boolean | string[] | number[]
export type DynamicFormAnswers = Record<string, DynamicFormValue>

export interface DynamicFormOption {
  label: string
  value: string
}

export interface DynamicFormField {
  name: string
  label: string
  type: 'text' | 'textarea' | 'number' | 'select' | 'multi_select' | 'boolean' | 'date' | 'candidate_select' | 'candidate_multi_select'
  required?: boolean
  placeholder?: string
  help_text?: string
  min_selected?: number
  options?: DynamicFormOption[]
  default?: DynamicFormValue | null
}

export interface DynamicCandidateOption {
  id: number
  name: string
  role?: string
  email?: string
}

export interface DynamicFormStep {
  id: string
  title: string
  description?: string
  fields: DynamicFormField[]
}

export interface DynamicFormSchema {
  version: 1
  form_id: string
  title: string
  description?: string
  submit_label?: string
  steps: DynamicFormStep[]
}

interface DynamicFormCardProps {
  form: DynamicFormSchema
  language: string
  disabled?: boolean
  completed?: boolean
  loadCandidates?: (search: string) => Promise<DynamicCandidateOption[]>
  onSubmit: (answers: DynamicFormAnswers) => Promise<void> | void
}

const MAX_FIELDS_PER_CARD = 2

export const paginateDynamicFormSteps = (
  steps: DynamicFormStep[],
  maxFieldsPerCard = MAX_FIELDS_PER_CARD
): DynamicFormStep[] => {
  const pageSize = Math.max(1, maxFieldsPerCard)
  return steps.flatMap((step) => {
    if (step.fields.length <= pageSize) return [step]

    const pages: DynamicFormStep[] = []
    for (let offset = 0; offset < step.fields.length; offset += pageSize) {
      const pageIndex = Math.floor(offset / pageSize)
      pages.push({
        ...step,
        id: `${step.id}_page_${pageIndex + 1}`,
        title: pageIndex === 0 ? step.title : `${step.title} · ${pageIndex + 1}`,
        description: pageIndex === 0 ? step.description : undefined,
        fields: step.fields.slice(offset, offset + pageSize),
      })
    }
    return pages
  })
}

const initialAnswers = (form: DynamicFormSchema): DynamicFormAnswers => {
  const answers: DynamicFormAnswers = {}
  form.steps.forEach((step) => {
    step.fields.forEach((field) => {
      if (field.default !== undefined && field.default !== null) {
        answers[field.name] = field.default
      } else if (field.type === 'multi_select' || field.type === 'candidate_multi_select') {
        answers[field.name] = []
      }
    })
  })
  return answers
}

const hasValue = (value: DynamicFormValue | undefined): boolean => {
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'boolean') return true
  return value !== undefined && String(value).trim().length > 0
}

export function DynamicFormCard({ form, language, disabled, completed, loadCandidates, onSubmit }: DynamicFormCardProps) {
  const locale = language.startsWith('fr') ? 'fr' : 'en'
  const displaySteps = useMemo(() => paginateDynamicFormSteps(form.steps), [form.steps])
  const [stepIndex, setStepIndex] = useState(0)
  const [answers, setAnswers] = useState<DynamicFormAnswers>(() => initialAnswers(form))
  const [submitting, setSubmitting] = useState(false)
  const [candidateSearch, setCandidateSearch] = useState('')
  const [candidateOptions, setCandidateOptions] = useState<DynamicCandidateOption[]>([])
  const [loadingCandidates, setLoadingCandidates] = useState(false)
  const currentStep = displaySteps[stepIndex]
  const isLastStep = stepIndex === displaySteps.length - 1
  const progress = ((stepIndex + 1) / displaySteps.length) * 100
  const canContinue = useMemo(
    () => currentStep.fields.every((field) => {
      const minSelected = Number(field.min_selected || 0)
      if (field.type === 'candidate_multi_select' || field.type === 'multi_select') {
        const selected = Array.isArray(answers[field.name]) ? answers[field.name] as Array<string | number> : []
        const requiredCount = Math.max(minSelected, field.required ? 1 : 0)
        return selected.length >= requiredCount
      }
      return !field.required || hasValue(answers[field.name])
    }),
    [answers, currentStep]
  )

  const candidateField = currentStep.fields.find((field) => (
    field.type === 'candidate_select' || field.type === 'candidate_multi_select'
  ))

  useEffect(() => {
    if (!candidateField || !loadCandidates) return
    let active = true
    const timeout = setTimeout(async () => {
      setLoadingCandidates(true)
      try {
        const options = await loadCandidates(candidateSearch)
        if (active) setCandidateOptions(options)
      } catch {
        if (active) setCandidateOptions([])
      } finally {
        if (active) setLoadingCandidates(false)
      }
    }, candidateSearch ? 250 : 0)
    return () => {
      active = false
      clearTimeout(timeout)
    }
  }, [candidateField?.name, candidateSearch, loadCandidates])

  const update = (name: string, value: DynamicFormValue) => {
    setAnswers((previous) => ({ ...previous, [name]: value }))
  }

  const submit = async () => {
    if (!canContinue || disabled || submitting) return
    setSubmitting(true)
    try {
      await onSubmit(answers)
    } finally {
      setSubmitting(false)
    }
  }

  if (completed) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:border-green-900 dark:bg-green-950/30 dark:text-green-300">
        <CheckCircle className="h-4 w-4" />
        {locale === 'fr' ? 'Formulaire envoyé.' : 'Form submitted.'}
      </div>
    )
  }

  const renderField = (field: DynamicFormField) => {
    const value = answers[field.name]
    const label = (
      <span>
        {field.label}
        {field.required && <span className="ml-1 text-red-500">*</span>}
      </span>
    )

    if (field.type === 'candidate_select' || field.type === 'candidate_multi_select') {
      const isMulti = field.type === 'candidate_multi_select'
      const selectedIds = isMulti
        ? (Array.isArray(value) ? value.map((item) => Number(item)).filter((id) => id > 0) : [])
        : []
      const selectedId = !isMulti && (typeof value === 'number' ? value : Number(value || 0))
      const minSelected = Number(field.min_selected || (field.required ? 1 : 0))
      const toggleCandidate = (candidateId: number) => {
        if (!isMulti) {
          update(field.name, candidateId)
          return
        }
        const next = selectedIds.includes(candidateId)
          ? selectedIds.filter((id) => id !== candidateId)
          : [...selectedIds, candidateId]
        update(field.name, next)
      }
      return (
        <div key={field.name} className="space-y-3">
          <div>
            <p className="text-sm font-medium">{label}</p>
            {field.help_text && <p className="mt-1 text-xs text-muted-foreground">{field.help_text}</p>}
            {isMulti && (
              <p className="mt-1 text-xs text-muted-foreground">
                {locale === 'fr'
                  ? `${selectedIds.length} sélectionné${selectedIds.length > 1 ? 's' : ''}${minSelected > 1 ? ` · ${minSelected} minimum` : ''}`
                  : `${selectedIds.length} selected${minSelected > 1 ? ` · ${minSelected} minimum` : ''}`}
              </p>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={candidateSearch}
              onChange={(event) => setCandidateSearch(event.target.value)}
              placeholder={field.placeholder || (locale === 'fr' ? 'Rechercher un candidat…' : 'Search candidates…')}
              className="pl-9"
            />
            {loadingCandidates && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
          </div>
          <div className="max-h-64 space-y-2 overflow-y-auto rounded-lg border border-border p-2">
            {!field.required && !isMulti && (
              <button
                type="button"
                onClick={() => update(field.name, '')}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${!selectedId ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}`}
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
                  <UserRound className="h-4 w-4" />
                </div>
                <div>
                  <p className="font-medium">{locale === 'fr' ? 'Continuer sans candidat' : 'Continue without a candidate'}</p>
                  <p className="text-xs text-muted-foreground">{locale === 'fr' ? 'Vous pourrez fournir le contexte du poste ensuite.' : 'You can provide the role context next.'}</p>
                </div>
              </button>
            )}
            {candidateOptions.map((candidate) => {
              const isSelected = isMulti ? selectedIds.includes(candidate.id) : selectedId === candidate.id
              return (
                <button
                  key={candidate.id}
                  type="button"
                  onClick={() => toggleCandidate(candidate.id)}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}`}
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">
                    {candidate.name.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-medium">{candidate.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{candidate.role || candidate.email || (locale === 'fr' ? 'Profil candidat' : 'Candidate profile')}</p>
                  </div>
                </button>
              )
            })}
            {!loadingCandidates && candidateOptions.length === 0 && (
              <p className="px-3 py-4 text-center text-xs text-muted-foreground">
                {locale === 'fr' ? 'Aucun candidat trouvé.' : 'No candidates found.'}
              </p>
            )}
          </div>
        </div>
      )
    }

    if (field.type === 'textarea') {
      return (
        <label key={field.name} className="block space-y-1.5 text-sm font-medium">
          {label}
          <textarea
            rows={4}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => update(field.name, event.target.value)}
            placeholder={field.placeholder}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {field.help_text && <span className="block text-xs font-normal text-muted-foreground">{field.help_text}</span>}
        </label>
      )
    }

    if (field.type === 'select') {
      return (
        <label key={field.name} className="block space-y-1.5 text-sm font-medium">
          {label}
          <select
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => update(field.name, event.target.value)}
            className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">{field.placeholder || (locale === 'fr' ? 'Sélectionner…' : 'Select…')}</option>
            {(field.options || []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          {field.help_text && <span className="block text-xs font-normal text-muted-foreground">{field.help_text}</span>}
        </label>
      )
    }

    if (field.type === 'multi_select') {
      const selected = Array.isArray(value) ? value : []
      return (
        <div key={field.name} className="space-y-2">
          <p className="text-sm font-medium">{label}</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {(field.options || []).map((option) => {
              const checked = selected.includes(option.value)
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => update(field.name, checked ? selected.filter((item) => item !== option.value) : [...selected, option.value])}
                  className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${checked ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}
                >
                  {option.label}
                </button>
              )
            })}
          </div>
          {field.help_text && <p className="text-xs text-muted-foreground">{field.help_text}</p>}
        </div>
      )
    }

    if (field.type === 'boolean') {
      return (
        <div key={field.name} className="space-y-2">
          <p className="text-sm font-medium">{label}</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { value: true, label: locale === 'fr' ? 'Oui' : 'Yes' },
              { value: false, label: locale === 'fr' ? 'Non' : 'No' },
            ].map((option) => (
              <button
                key={String(option.value)}
                type="button"
                onClick={() => update(field.name, option.value)}
                className={`rounded-lg border px-3 py-2 text-sm ${value === option.value ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted'}`}
              >
                {option.label}
              </button>
            ))}
          </div>
          {field.help_text && <p className="text-xs text-muted-foreground">{field.help_text}</p>}
        </div>
      )
    }

    return (
      <label key={field.name} className="block space-y-1.5 text-sm font-medium">
        {label}
        <Input
          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
          value={value === undefined ? '' : String(value)}
          onChange={(event) => update(field.name, field.type === 'number' && event.target.value !== '' ? Number(event.target.value) : event.target.value)}
          placeholder={field.placeholder}
          className="mt-1"
        />
        {field.help_text && <span className="block text-xs font-normal text-muted-foreground">{field.help_text}</span>}
      </label>
    )
  }

  return (
    <div className="overflow-hidden rounded-xl border border-primary/20 bg-background shadow-sm">
      <div className="border-b border-border bg-primary/5 px-5 py-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <ClipboardList className="h-5 w-5 shrink-0 text-primary" />
            <h3 className="truncate font-semibold">{form.title}</h3>
          </div>
          <span className="shrink-0 text-xs font-medium text-muted-foreground">{stepIndex + 1} / {displaySteps.length}</span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progress}%` }} />
        </div>
        {form.description && <p className="mt-3 text-xs text-muted-foreground">{form.description}</p>}
      </div>

      <div className="space-y-5 p-5">
        <div>
          <h4 className="font-semibold text-foreground">{currentStep.title}</h4>
          {currentStep.description && <p className="mt-1 text-xs text-muted-foreground">{currentStep.description}</p>}
        </div>
        <div className="space-y-4">{currentStep.fields.map(renderField)}</div>

        <div className="flex items-center justify-between border-t border-border pt-4">
          <Button type="button" variant="ghost" onClick={() => setStepIndex((value) => Math.max(0, value - 1))} disabled={stepIndex === 0 || disabled || submitting}>
            <ArrowLeft className="mr-2 h-4 w-4" /> {locale === 'fr' ? 'Retour' : 'Back'}
          </Button>
          {isLastStep ? (
            <Button type="button" onClick={submit} disabled={!canContinue || disabled || submitting} loading={submitting}>
              {form.submit_label || (locale === 'fr' ? 'Envoyer' : 'Submit')}
            </Button>
          ) : (
            <Button type="button" onClick={() => setStepIndex((value) => value + 1)} disabled={!canContinue || disabled || submitting}>
              {locale === 'fr' ? 'Suivant' : 'Next'} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
